/**
 * Persisted progress for every onboarding tour — one key holding one map.
 *
 * `{ shell: 'done', table: 'tableEdit' }`: per tour, either the step key the user
 * is on or `done` once they finish or skip. One entry per tour rather than one
 * key per tour keeps storage to a single place as tours are added, and a value
 * that is either a step or `done` makes "finished but saved on step 3"
 * unrepresentable.
 *
 * So an interrupted tour resumes rather than restarting or vanishing: the step is
 * written as it advances, and only reaching the end or skipping writes `done`.
 *
 * `localStorage` plus a custom `window` event, mirroring
 * `oldDashboardBannerState`, so the tour and any control that replays it stay in
 * sync within the tab (`storage` only fires in *other* tabs).
 */

const KEY = 'retraced:v2Tours';
const EVENT = 'retraced:v2TourChange';
const DONE = 'done';

type TourMap = Record<string, string>;

function read(): TourMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // Hand-edited or half-written values must not throw on every render.
    return parsed && typeof parsed === 'object' ? (parsed as TourMap) : {};
  } catch {
    return {};
  }
}

function write(next: TourMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Ignore: the event still syncs this tab, the tour just won't resume.
  }
  window.dispatchEvent(new Event(EVENT));
}

/**
 * Whether storage is usable at all.
 *
 * In private mode every tour reads as unfinished, which would reopen it on every
 * navigation — worse than never opening. Checked once per read rather than
 * assumed, so a working browser isn't penalised.
 */
function storageBlocked(): boolean {
  try {
    localStorage.getItem(KEY);
    return false;
  } catch {
    return true;
  }
}

export function isTourDone(tourId: string): boolean {
  if (storageBlocked()) return true;
  return read()[tourId] === DONE;
}

/**
 * The step key to resume `tourId` on, or null to start from the beginning.
 *
 * A key rather than an index: step lists grow as the app gains controls, and an
 * index would silently resume someone onto whatever step now sits there.
 */
export function getTourStep(tourId: string): string | null {
  const value = read()[tourId];
  return !value || value === DONE ? null : value;
}

export function setTourStep(tourId: string, stepKey: string): void {
  write({ ...read(), [tourId]: stepKey });
}

/** Both exits go through here, so an abandoned tour is as final as a completed
 *  one and neither leaves a stale step behind. */
export function markTourDone(tourId: string): void {
  write({ ...read(), [tourId]: DONE });
}

/** Clears one tour, or all of them, so they run again from step one — for a
 *  "replay the tour" control. */
export function resetTours(tourId?: string): void {
  if (!tourId) {
    write({});
    return;
  }
  const next = read();
  delete next[tourId];
  write(next);
}

export function subscribeTour(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  return () => window.removeEventListener(EVENT, onChange);
}
